import { IWebsiteBuilderFormItemOptionRaw, IWebsiteBuilderFormItemRaw, IWebsiteBuilderFormRaw, IWebsiteBuilderFormRowRaw } from "redux/reducers/models/websiteBuilder";
import { TFormBuilderButtonRole } from "../../editor/model/item/formBuilderButton";
import { TFormBuilderCheckboxRole } from "../../editor/model/item/formBuilderCheckbox";
import { TFormBuilderDateRole } from "../../editor/model/item/formBuilderDate";
import { TFormBuilderInputRole } from "../../editor/model/item/formBuilderInput";
import { isWebsiteBuilderFormItemRawValid, TFormBuilderItem, TFormBuilderItemType } from "../../editor/model/item/formBuilderItem";
import { IFormBuilderItemOption, isWebsiteBuilderFormItemOptionRawValid } from "../../editor/model/item/formBuilderItemOption";
import { TFormBuilderSelectRole } from "../../editor/model/item/formBuilderSelect";
import { TFormBuilderTextRole } from "../../editor/model/item/formBuilderText";
import { TFormBuilderTextareaRole } from "../../editor/model/item/formBuilderTextarea";
import { IFormBuilderForm, isWebsiteBuilderFormRawValid } from "../../editor/model/formBuilderForm";
import { IFormBuilderRow, isFormBuilderRowAlignItems, isFormBuilderRowJustifyContent, isWebsiteBuilderFormRowRawValid } from "../../editor/model/formBuilderRow";

function buildForm(rawForm: IWebsiteBuilderFormRaw): IFormBuilderForm {
  const children = rawForm.rows
    .filter(isWebsiteBuilderFormRowRawValid)
    .sort((prev, next) => prev.position - next.position)
    .map(buildRow);
  
  return {
    id: rawForm.id,
    key: rawForm.key,
    websiteId: rawForm.website_id,
    updatedAt: rawForm.updated_at,
    archivedAt: rawForm.archived_at,
    settings: {
      name: rawForm.name ?? "",
      emailNotificationsEnabled: rawForm.email_notification_enabled ?? false,
    },
    children,
  };
}

function buildRow(rawRow: IWebsiteBuilderFormRowRaw): IFormBuilderRow {
  const children = rawRow.items
    .filter(isWebsiteBuilderFormItemRawValid)
    .sort((prev, next) => prev.position - next.position)
    .map(buildItem);

  return {
    id: rawRow.id,
    key: rawRow.key,
    type: "row",
    data: {
      formId: rawRow.form_id,
      settings: {
        justifyContent: isFormBuilderRowJustifyContent(rawRow.justify_content) ? rawRow.justify_content : "flex-start",
        alignItems: isFormBuilderRowAlignItems(rawRow.align_items) ? rawRow.align_items : "flex-start",
      },
    },
    children,
  };
}

function buildItem(rawItem: IWebsiteBuilderFormItemRaw): TFormBuilderItem {
  const type = rawItem.type as TFormBuilderItemType;
  
  switch (type) {
    case "button":
      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "button",
          role: rawItem.role as TFormBuilderButtonRole,
          settings: {
            label: rawItem.label ?? "",
          },
        },
      };
    case "checkbox":
      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "checkbox",
          role: rawItem.role as TFormBuilderCheckboxRole,
          settings: {
            label: rawItem.label ?? "",
          },
        },
      };
    case "date":
      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "date",
          role: rawItem.role as TFormBuilderDateRole,
          settings: {
            label: rawItem.label ?? "",
            required: rawItem.required ?? false,
          },
        },
      };
    case "input": {
      const role = rawItem.role as TFormBuilderInputRole;

      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "input",
          role,
          settings: {
            label: rawItem.label ?? "",
            placeholder: rawItem.placeholder ?? "",
            required: rawItem.required ?? false,
            ...(role === "custom" ? { isNumeric: rawItem.is_numeric ?? false } : {}),
          },
        },
      };
    } case "select": {
      const options = rawItem.options
        .filter(isWebsiteBuilderFormItemOptionRawValid)
        .sort((prev, next) => prev.position - next.position)
        .map(buildItemOption);

      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "select",
          role: rawItem.role as TFormBuilderSelectRole,
          settings: {
            label: rawItem.label ?? "",
            placeholder: rawItem.placeholder ?? "",
            required: rawItem.required ?? false,
            options,
          },
        },
      };
    }
    case "text":
      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "text",
          role: rawItem.role as TFormBuilderTextRole,
          settings: {
            content: rawItem.content ?? "",
          },
        },
      };
    case "textarea":
      return {
        id: rawItem.id,
        key: rawItem.key,
        type: "item",
        data: {
          formRowId: rawItem.form_row_id,
          type: "textarea",
          role: rawItem.role as TFormBuilderTextareaRole,
          settings: {
            label: rawItem.label ?? "",
            placeholder: rawItem.placeholder ?? "",
            required: rawItem.required ?? false,
          },
        },
      };
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = type;
      return null;
    }
  }
}

function buildItemOption(rawItemOption: IWebsiteBuilderFormItemOptionRaw): IFormBuilderItemOption {
  return {
    id: rawItemOption.id,
    formItemId: rawItemOption.form_item_id,
    label: rawItemOption.label ?? "",
    value: rawItemOption.value ?? "",
  };
}

export function formBuilderBuildForm(rawForm: IWebsiteBuilderFormRaw): IFormBuilderForm {
  if (isWebsiteBuilderFormRawValid(rawForm)) {
    return buildForm(rawForm);
  } else {
    return null;
  }
}

export function formBuilderBuildForms(rawForms: IWebsiteBuilderFormRaw[]): IFormBuilderForm[] {
  const forms: IFormBuilderForm[] = [];

  for (const rawForm of rawForms) {
    if (isWebsiteBuilderFormRawValid(rawForm)) {
      forms.push(buildForm(rawForm));
    }
  }
  
  return forms;
}